import React, { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { ProductContext } from "../context/ProductContext";
import Header from "../layouts/header/Header";
import { useNavigation } from "../utils/navigation";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/footer/Footer";
import { useTranslation } from "react-i18next";

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  background-color: var(--bg-100);
  min-height: 100vh;
`;

const bounceIn = keyframes`
  0% { transform: scale(0.3); opacity: 0; }
  50% { transform: scale(1.08); opacity: 1; }
  70% { transform: scale(0.94); }
  100% { transform: scale(1); }
`;
const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const BadgeCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: var(--success-color);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${bounceIn} 0.6s cubic-bezier(0.22, 1, 0.36, 1);
`;

const Check = styled.path`
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
  animation: ${draw} 0.35s 0.5s ease-out forwards;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--text-100);
  margin-bottom: 10px;
  margin-top: 16px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-200);
  margin-bottom: 20px;
`;

const OrderNumberBadge = styled.div`
  background-color: var(--bg-200);
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 0.9rem;
  color: var(--text-200);
  margin-bottom: 20px;

  strong {
    color: var(--text-100);
  }
`;

const OrderDetails = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: var(--bg-200);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  margin-top: 20px;
  padding: 20px 0;
  @media (min-width: 1040px) {
    padding: 34px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const SectionLabel = styled.h3`
  width: 94%;
  text-align: left;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-200);
  margin: 14px 0 6px;

  &:first-child {
    margin-top: 0;
  }
`;

const OrderItem = styled.div`
  width: 94%;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--bg-100);
  padding: 10px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const OrderInfo = styled.div`
  font-size: 1rem;
  color: var(--text-200);
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const AddressBlock = styled.div`
  width: 94%;
  text-align: left;
  color: var(--text-200);
  padding: 10px 0 16px;
  line-height: 1.5;
`;

const Button = styled.button`
  width: 100%;
  margin: 8px 0;
`;

const SecondaryButton = styled.button`
  width: 100%;
  margin: 8px 0;
  background-color: transparent;
  border: 1px solid var(--text-200);
  color: var(--text-100);
`;

const ThankYouPage = () => {
  const { t } = useTranslation();
  const { createdOrder } = useContext(ProductContext);
  const { goToHome, goToOrderHistory } = useNavigation();
  const navigate = useNavigate();

  const address = createdOrder?.address;

  return (
    <>
      <Header />
      <ThankYouContainer>
        <BadgeCircle>
          <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
            <Check
              d="M8 17l6 6 12-13"
              stroke="var(--bg-200)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </BadgeCircle>
        <Title>{t("THANK_YOU.TITLE")}</Title>
        <Subtitle>{t("THANK_YOU.SUBTITLE")}</Subtitle>
        {createdOrder && (
          <OrderNumberBadge>
            {t("THANK_YOU.ORDER_DETAILS")} <strong>#{createdOrder.id}</strong>
          </OrderNumberBadge>
        )}

        {createdOrder && (
          <OrderDetails>
            <SectionLabel>{t("THANK_YOU.LOGISTICS", "Delivery")}</SectionLabel>
            <OrderItem>
              <OrderInfo>
                <BoldText>{t("THANK_YOU.PAYMENT_METHOD")}</BoldText>
              </OrderInfo>
              <OrderInfo>{createdOrder.payment_method}</OrderInfo>
            </OrderItem>
            <OrderItem>
              <OrderInfo>
                <BoldText>{t("THANK_YOU.TRANSPORT_METHOD")}</BoldText>
              </OrderInfo>
              <OrderInfo>{createdOrder.transport_method}</OrderInfo>
            </OrderItem>

            {address && (
              <>
                <SectionLabel>
                  {t("THANK_YOU.SHIPPING_ADDRESS", "Shipping address")}
                </SectionLabel>
                <AddressBlock>
                  {address.street} {address.building_number}
                  {address.secondary_street ? `, ${address.secondary_street}` : ""}
                  <br />
                  {address.postal_code} {address.city}, {address.country}
                  {address.phone_number && (
                    <>
                      <br />
                      {address.phone_number}
                    </>
                  )}
                </AddressBlock>
              </>
            )}

            <SectionLabel>{t("THANK_YOU.TOTAL_PAID", "Total")}</SectionLabel>
            <OrderItem>
              <OrderInfo>
                <BoldText>{t("THANK_YOU.TOTAL", "Total")}</BoldText>
              </OrderInfo>
              <OrderInfo>
                {createdOrder.total_price} {createdOrder.currency}
              </OrderInfo>
            </OrderItem>

            <Button onClick={() => navigate(goToOrderHistory())}>
              {t("THANK_YOU.VIEW_ORDER", "View Order")}
            </Button>
            <SecondaryButton onClick={() => navigate(goToHome())}>
              {t("THANK_YOU.BUTTON")}
            </SecondaryButton>
          </OrderDetails>
        )}
      </ThankYouContainer>
      <Footer />
    </>
  );
};

export default ThankYouPage;
